/**
 * Product Mix Report Table Component
 * @see Sprint - Rapport Product Mix
 *
 * Uses DataGrid with:
 * - Columns: Article, Qte vendue, CA net HT, FC theorique HT, FC%
 * - Grouping by POS category with subtotals
 * - Grand total row
 * - Export Excel with native dataType formatting
 * - Financial columns hidden via showFinancials prop
 */

import { useMemo } from 'react';
import {
  DataGrid,
  textColumn,
  integerColumn,
  moneyColumn,
  percentColumn,
  type DataGridColumn,
  type DataGridGrouping,
  type DataGridSummaryItem,
} from '@bill/ui';
import { useCurrency, useBrandContext } from '@/hooks';
import type { ProductMixRow } from '@bstock/shared';

interface ProductMixTableProps {
  rows: ProductMixRow[];
  isLoading: boolean;
  showFinancials?: boolean;
}

export function ProductMixTable({
  rows,
  isLoading,
  showFinancials = false,
}: ProductMixTableProps) {
  const currency = useCurrency();
  const { brand } = useBrandContext();

  const exportFileName = useMemo(() => {
    const brandSlug = (brand?.name ?? 'export').replace(/\s+/g, '_');
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const ts = `${pad(now.getDate())}-${pad(now.getMonth() + 1)}-${String(now.getFullYear()).slice(2)}-${pad(now.getHours())}-${pad(now.getMinutes())}`;
    return `product_mix_${brandSlug}_${ts}`;
  }, [brand?.name]);

  const columns = useMemo<DataGridColumn<ProductMixRow>[]>(() => {
    const cols: DataGridColumn<ProductMixRow>[] = [
      textColumn<ProductMixRow>({
        id: 'posProductName',
        header: 'Article',
        accessor: 'posProductName',
        ellipsis: true,
        maxWidth: '300px',
      }),
      integerColumn<ProductMixRow>({
        id: 'quantitySold',
        header: 'Qte vendue',
        accessor: 'quantitySold',
      }),
    ];

    if (showFinancials) {
      cols.push(
        moneyColumn<ProductMixRow>({
          id: 'revenueNetCentimes',
          header: 'CA net HT',
          accessor: (r) => r.revenueNetCentimes / 100,
        }),
        moneyColumn<ProductMixRow>({
          id: 'theoreticalCostCentimes',
          header: 'FC theorique HT',
          accessor: (r) => r.theoreticalCostCentimes / 100,
        }),
        percentColumn<ProductMixRow>({
          id: 'foodCostPercent',
          header: 'FC %',
          // foodCostPercent is in [0..100], DataGrid percent expects [0..1]
          accessor: (r) => r.foodCostPercent / 100,
        })
      );
    }

    return cols;
  }, [showFinancials]);

  const grouping = useMemo<DataGridGrouping<ProductMixRow>>(() => ({
    groupBy: 'posCategory',
    defaultExpanded: 'all',
    ungroupedLabel: 'Sans categorie',
  }), []);

  const summary = useMemo<DataGridSummaryItem<ProductMixRow>[]>(() => {
    const items: DataGridSummaryItem<ProductMixRow>[] = [
      {
        columnId: 'quantitySold',
        aggregate: 'sum',
        value: (r) => r.quantitySold,
      },
    ];

    if (showFinancials) {
      items.push(
        {
          columnId: 'revenueNetCentimes',
          aggregate: 'sum',
          value: (r) => r.revenueNetCentimes / 100,
        },
        {
          columnId: 'theoreticalCostCentimes',
          aggregate: 'sum',
          value: (r) => r.theoreticalCostCentimes / 100,
        },
        {
          columnId: 'foodCostPercent',
          aggregate: 'avg',
          value: (r) => r.foodCostPercent / 100,
        }
      );
    }

    return items;
  }, [showFinancials]);

  return (
    <DataGrid<ProductMixRow>
      data={rows}
      columns={columns}
      rowKey={(r) => r.posProductId}
      isLoading={isLoading}
      skeletonRows={8}
      grouping={grouping}
      summary={summary}
      showGrandTotal
      currency={currency}
      maxHeight="80vh"
      exportable={{
        fileName: exportFileName,
        sheetName: 'Product Mix',
        position: 'toolbar',
      }}
      emptyState={
        <div className="py-8 text-center text-sm text-neutral-500">
          Aucune donnee pour cette periode
        </div>
      }
    />
  );
}
